// The only module that builds clients over @kk/api-client (the HttpClient is scope-agnostic; createApiClients and
// DevTokenAuthClient are parent-only and stay unused here).
import { HttpClient, type FetchLike } from '@kk/api-client';
import type { Clock, SessionStorage, TokenProvider } from '@kk/domain';

import { EmailStaffAuthClient } from '@/auth/EmailStaffAuthClient';
import { DevTokenStaffAuthClient, type StaffAuthClient } from '@/auth/StaffAuthClient';
import { auth0Config } from '@/lib/env';
import { isAdminHost } from '@/lib/host';

import { StaffApiClient, type StaffDataClient } from './StaffApiClient';

export interface StaffClientsOptions {
  baseUrl: string;
  /** Reads the LIVE session's access token (SessionProvider owns it). */
  getAccessToken: () => Promise<string | null>;
  sessionStorage: SessionStorage;
  fetch?: FetchLike;
  clock?: Clock;
  timeoutMs?: number;
}

export interface StaffClients {
  data: StaffDataClient;
  auth: StaffAuthClient;
  clock: Clock;
}

export function createStaffClients(opts: StaffClientsOptions): StaffClients {
  const clock: Clock = opts.clock ?? { now: () => new Date() };
  // @kk/domain's TokenProvider scope is still 'parent' | 'child'; HttpClient only calls getAccessToken. The cast
  // goes away once domain gains the staff scope (research-stack.md §9).
  const tokenProvider = {
    scope: 'staff',
    getAccessToken: opts.getAccessToken,
  } as unknown as TokenProvider;
  const http = new HttpClient({
    baseUrl: opts.baseUrl,
    fetch: opts.fetch,
    tokenProvider,
    timeoutMs: opts.timeoutMs,
  });
  /*
   * **AUTH0, OR NOTHING AT ALL IN A PRODUCTION BUILD.**
   *
   * This used to be a ternary falling back to the dev-token stub, and on 2026-09-22 that shipped:
   * clubs.kiddiekredit.com served an email-and-password form posting to `/dev/token`, a route the API does
   * not register in production, on a console that manages other people's children. Evan: *"a dev auth path
   * in a production bundle is not a degraded state."*
   *
   * **The `__DEV__` branch is how the stub leaves the BUNDLE, not just the code path.** Vite defines
   * `__DEV__` as a literal `false` for production, so this becomes `else if (false)`, the only reference to
   * `DevTokenStaffAuthClient` is dead, and Rollup drops the class — which is why `/dev/token` no longer
   * appears in the artefact at all. A ternary keeps it referenced and therefore bundled, however carefully
   * the runtime avoids calling it; that is the difference between not USING a dev auth path and not SHIPPING
   * one, and only the second can be checked from outside.
   */
  const a0 = auth0Config();
  const deps = { http, storage: opts.sessionStorage, clock };
  let auth: StaffAuthClient;
  // our own email-first sign-in (O00, O00c, O00d); Auth0 is now only the road to Google
  if (a0)
    auth = new EmailStaffAuthClient({
      ...deps,
      site: isAdminHost() ? 'admin' : 'clubs',
      google: a0,
    });
  else if (__DEV__) auth = new DevTokenStaffAuthClient(deps);
  else
    throw new Error(
      'the portal was built without Auth0: set VITE_AUTH0_DOMAIN, VITE_AUTH0_CLIENT_ID and ' +
        'VITE_AUTH0_AUDIENCE (infra/portal-production.env). It will not fall back to the dev sign-in.',
    );
  return { data: new StaffApiClient(http), auth, clock };
}
